import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FileKey } from 'lucide-react'
import { downloadWalletBackup, walletBackupText } from 'lib/walletBackup'
import {
    loadWalletBackupStatus,
    readWalletBackupFile,
    restoreWalletFromBackupText,
    verifyWalletBackupText,
    type WalletBackupStatus,
} from 'lib/walletFileBackup'
import styles from './wallet.module.scss'

type PendingAction = 'verify' | 'restore'

// Safari only allows clipboard writes inside the click; a ClipboardItem can take
// the still-loading text as a promise, so start the write before awaiting it.
async function copyText(text: Promise<string>) {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
            new ClipboardItem({
                'text/plain': text.then(value => new Blob([value], { type: 'text/plain' })),
            }),
        ])
        return
    }
    await navigator.clipboard.writeText(await text)
}

function errorMessage(error: unknown) {
    return error instanceof Error
        ? error.message
        : 'Something went wrong. Your wallet was not changed.'
}

export default function WalletFileBackup({
    userId,
    canExport,
    onSaved,
}: {
    userId: string
    // undefined while the parent is still reading this device's keys.
    canExport: boolean | undefined
    onSaved?: () => Promise<void>
}) {
    const [status, setStatus] = useState<WalletBackupStatus | null>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')
    const [restored, setRestored] = useState(false)
    const [pasteOpen, setPasteOpen] = useState(false)
    const [pasted, setPasted] = useState('')
    const fileInput = useRef<HTMLInputElement>(null)
    const fileAction = useRef<PendingAction>('verify')
    const mounted = useRef(false)
    const inFlight = useRef(false)

    const reload = useCallback(async () => {
        const result = await loadWalletBackupStatus()
        if (mounted.current) setStatus(result)
    }, [])

    useEffect(() => {
        mounted.current = true
        reload().catch(err => {
            if (mounted.current) setError(errorMessage(err))
        })
        return () => {
            mounted.current = false
        }
    }, [reload])

    const run = async (action: () => Promise<string | void>) => {
        if (inFlight.current) return
        inFlight.current = true
        setBusy(true)
        setError('')
        setNotice('')
        try {
            const message = await action()
            if (mounted.current && message) setNotice(message)
        } catch (err) {
            if (mounted.current) setError(errorMessage(err))
        } finally {
            inFlight.current = false
            if (mounted.current) setBusy(false)
        }
    }

    const applyBackupText = (action: PendingAction, walletText: string) =>
        run(async () => {
            if (action === 'verify') {
                const result = await verifyWalletBackupText(walletText)
                if (mounted.current) setStatus(result)
                setPasteOpen(false)
                setPasted('')
                return 'Backup verified. It opens this wallet with both of its keys.'
            }
            if (
                !window.confirm(
                    'Restore this wallet on this device from the backup? Your current wallet keys stay the same; this device gets a fresh registration.',
                )
            ) {
                return
            }
            await restoreWalletFromBackupText(walletText)
            setPasteOpen(false)
            setPasted('')
            if (mounted.current) setRestored(true)
            await reload().catch(() => {})
            return 'Your wallet was restored from the backup. Reload Treechat to use your wallet again.'
        })

    // Default to verification when a share exists, but keep file restore available:
    // the share alone cannot recover a missing wallet payload or server share.
    const action: PendingAction = canExport ? 'verify' : 'restore'

    const chooseFile = (pendingAction: PendingAction) => {
        fileAction.current = pendingAction
        fileInput.current?.click()
    }

    const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        try {
            await applyBackupText(fileAction.current, await readWalletBackupFile(file))
        } catch (err) {
            setError(errorMessage(err))
        }
    }

    const verifiedAt = status?.verified_at
        ? new Date(status.verified_at).toLocaleDateString()
        : null

    return (
        <section className={styles.selfCustodySetupCard} aria-label="Wallet backup file">
            <div className={styles.selfCustodySetupHeader}>
                <FileKey size={20} aria-hidden="true" />
                <div>
                    <div className={styles.selfCustodySetupTitle}>Wallet backup file</div>
                    <p className={styles.selfCustodySetupSubtitle}>
                        The backup file holds both wallet keys, unencrypted. Anyone with it can
                        spend your funds, so keep it somewhere private such as a password manager or
                        offline storage. A copy kept only on this computer protects against clearing
                        browser data, not against losing the computer.
                    </p>
                </div>
            </div>

            <p className={styles.selfCustodySetupHint} role="status">
                {canExport === undefined
                    ? 'Checking this device for your wallet keys…'
                    : !canExport
                    ? "This device doesn't hold your wallet keys. Open your backup file, or paste it, to use your wallet here."
                    : verifiedAt
                    ? `Backup verified for this wallet on ${verifiedAt}.`
                    : 'No verified backup yet. Save a backup, then verify it by opening the saved file.'}
            </p>
            {error && (
                <p className={styles.selfCustodySetupError} role="alert">
                    {error}
                </p>
            )}
            {notice && <p role="status">{notice}</p>}
            {restored && (
                <button className={styles.secondaryBtn} onClick={() => window.location.reload()}>
                    Reload Treechat
                </button>
            )}

            {/* Bootstrap's input[type="file"] { display: block } beats the hidden attribute. */}
            <input
                ref={fileInput}
                type="file"
                accept=".json,application/json"
                style={{ display: 'none' }}
                aria-hidden="true"
                tabIndex={-1}
                onChange={onFile}
            />

            {!restored && canExport !== undefined && (
                <>
                    {canExport && (
                        <div className={styles.selfCustodySetupActions}>
                            <button
                                className={styles.secondaryBtn}
                                disabled={busy}
                                onClick={() =>
                                    void run(async () => {
                                        await downloadWalletBackup('client_shares', userId)
                                        await onSaved?.()
                                        return 'Backup saved. Now verify it by opening the saved file.'
                                    })
                                }
                            >
                                Save backup file
                            </button>
                            <button
                                className={styles.secondaryBtn}
                                disabled={busy}
                                onClick={() =>
                                    void run(async () => {
                                        if (
                                            !window.confirm(
                                                'Copy both wallet keys to the clipboard? Paste them only somewhere private, then clear the clipboard.',
                                            )
                                        ) {
                                            return
                                        }
                                        await copyText(walletBackupText('client_shares', userId))
                                        setPasteOpen(true)
                                        return 'Backup copied. Verify it by pasting it back below.'
                                    })
                                }
                            >
                                Copy instead
                            </button>
                        </div>
                    )}
                    <div className={styles.selfCustodySetupActions}>
                        <button
                            className={styles.secondaryBtn}
                            disabled={busy || !status}
                            onClick={() => chooseFile(action)}
                        >
                            {canExport ? 'Verify saved file' : 'Open backup file'}
                        </button>
                        <button
                            className={styles.secondaryBtn}
                            disabled={busy || !status}
                            onClick={() => setPasteOpen(open => !open)}
                        >
                            {pasteOpen
                                ? 'Hide paste'
                                : canExport
                                ? 'Verify by pasting'
                                : 'Paste backup'}
                        </button>
                    </div>
                    {canExport && (
                        <button
                            className={styles.secondaryBtn}
                            disabled={busy || !status}
                            onClick={() => chooseFile('restore')}
                        >
                            Restore from backup file
                        </button>
                    )}
                    {pasteOpen && (
                        <div>
                            <textarea
                                className={styles.selfCustodySetupTextarea}
                                aria-label="Pasted wallet backup"
                                autoComplete="off"
                                spellCheck={false}
                                value={pasted}
                                onChange={event => setPasted(event.target.value)}
                                placeholder="Paste the contents of your wallet backup file"
                            />
                            <div className={styles.selfCustodySetupActions}>
                                <button
                                    className={styles.secondaryBtn}
                                    disabled={busy || !pasted.trim()}
                                    onClick={() => void applyBackupText(action, pasted.trim())}
                                >
                                    {canExport
                                        ? 'Verify pasted backup'
                                        : 'Restore from pasted backup'}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    )
}
